/**
 * Список доставок курьера, сгруппированный по слотам.
 *
 * Курьер работает по времени, а не по дате создания: сервер отдаёт заказы
 * отсортированными по `slot_date, slot_start`, здесь они только разбиваются
 * на заголовки.
 */
import type { CurrentUser, Order } from '@arbuz/shared';
import { formatMoney, makeT, orderStatusLabel } from '../i18n';
import { haptic } from '../tg';
import { statusColor } from './courierUi';

interface Props {
  user: CurrentUser;
  orders: Order[] | null;
  scope: 'active' | 'completed';
  error: string | null;
  onScope: (s: 'active' | 'completed') => void;
  onOpen: (id: number) => void;
  onRetry: () => void;
}

/** Заказы одного слота идут под общим заголовком «дата, время». */
function groupBySlot(orders: Order[]): { key: string; title: string; items: Order[] }[] {
  const groups = new Map<string, { key: string; title: string; items: Order[] }>();

  for (const o of orders) {
    const key = `${o.slot_date} ${o.slot_start}`;
    const existing = groups.get(key);
    if (existing) {
      existing.items.push(o);
    } else {
      groups.set(key, {
        key,
        title: `${o.slot_date}, ${o.slot_start}–${o.slot_end}`,
        items: [o],
      });
    }
  }

  return [...groups.values()];
}

export function CourierOrders({
  user,
  orders,
  scope,
  error,
  onScope,
  onOpen,
  onRetry,
}: Props) {
  const t = makeT(user.locale);

  return (
    <main className="page courier-page">
      <div className="courier-topline">
        <div>
          <p className="courier-eyebrow">{t('orders')}</p>
          <h1 className="courier-title">
            {scope === 'active' ? t('ordersInWork') : t('orderHistory')}
          </h1>
        </div>
        {orders && (
          <span className="courier-pill">
            {orders.length} {scope === 'active' ? t('activeOrders') : t('completedOrders')}
          </span>
        )}
      </div>

      <div role="tablist" className="courier-tabs">
        {(['active', 'completed'] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={scope === s}
            className={`courier-tab${scope === s ? ' active' : ''}`}
            onClick={() => {
              haptic('light');
              onScope(s);
            }}
          >
            {s === 'active' ? t('activeOrders') : t('orderHistory')}
          </button>
        ))}
      </div>

      {/* Ошибка — отдельное состояние с действием, а не пустой список */}
      {error && (
        <div className="card" style={{ marginBottom: 'var(--s-3)' }}>
          <p role="alert" style={{ margin: 0, color: 'var(--c-danger)' }}>
            {error}
          </p>
          <button
            type="button"
            className="btn-primary"
            style={{ width: '100%', marginTop: 'var(--s-3)' }}
            onClick={onRetry}
          >
            {t('retry')}
          </button>
        </div>
      )}

      {orders === null ? (
        <p className="muted">…</p>
      ) : orders.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>
            {scope === 'active' ? t('courierNoActive') : t('courierNoCompleted')}
          </p>
          {scope === 'active' && (
            <p
              className="muted"
              style={{ margin: 'var(--s-2) 0 0', fontSize: 'var(--fs-sm)' }}
            >
              {t('courierNoActiveHint')}
            </p>
          )}
        </div>
      ) : (
        groupBySlot(orders).map((group) => (
          <section key={group.key} style={{ marginBottom: 'var(--s-6)' }}>
            <h2 className="courier-timeline-title">{group.title}</h2>

            <div className="courier-order-list">
              {group.items.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="card courier-order-row"
                  onClick={() => {
                    haptic('light');
                    onOpen(o.id);
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>#{o.public_number}</strong>
                    <strong>
                      {formatMoney(o.total, user.locale)} {t('sum')}
                    </strong>
                  </div>

                  <p style={{ margin: 'var(--s-2) 0 0', color: statusColor(o.status) }}>
                    {orderStatusLabel(o.status, user.locale)}
                  </p>

                  <p
                    className="muted"
                    style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}
                  >
                    {o.address_text}
                  </p>
                </button>
              ))}
            </div>
          </section>
        ))
      )}
    </main>
  );
}
